import type { User } from '@supabase/supabase-js'
import { createClient } from '@/lib/supabase/client'

/**
 * Routes reachable without a session. Everything else requires one.
 *
 * An allow-list rather than a deny-list: a new screen added later is protected
 * by default, and forgetting to list it locks people out loudly instead of
 * leaking data quietly.
 */
const PUBLIC_PATHS = [
  '/login',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  /* Not a screen — it redirects to /login (see routes/sign-in.tsx). Listed
     anyway, because otherwise the gate reaches it first and sends a signed-out
     visitor to `/login?next=/sign-in`, which after a successful sign-in walks
     them back through /sign-in to /login to `/`. Same destination, three
     pointless hops. */
  '/sign-in',
  /* Reachable without a session — but NOT an auth screen, so a signed-in
     visitor stays here rather than being bounced to `/`. See AUTH_PATHS. */
  '/invitations',
]

/**
 * The subset of public paths that exist ONLY to get someone signed in.
 *
 * Being signed in makes these meaningless, so the gate sends that visitor to
 * `/`. It used to apply that rule to every public path, which was the same
 * thing while the only public screens were sign-in, sign-up and password
 * recovery.
 *
 * `/invitations/:id` breaks that equivalence. A person following an invitation
 * link may already have an account and be signed in — quite likely, since the
 * invitation is often to somebody who already uses the product — and bouncing
 * them to `/` would drop the invitation entirely, leaving them staring at their
 * own workspace wondering what the email was for.
 */
const AUTH_PATHS = [
  '/login',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/sign-in',
]

export function isPublicPath(pathname: string) {
  return matches(PUBLIC_PATHS, pathname)
}

export function isAuthPath(pathname: string) {
  return matches(AUTH_PATHS, pathname)
}

function matches(paths: string[], pathname: string) {
  return paths.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  )
}

/**
 * The signed-in user, or null.
 *
 * `getSession()`, not `getUser()`, and the difference matters less here than it
 * used to. The old middleware used `getUser()` because it was making a TRUST
 * decision on the server and had to verify the token rather than believe the
 * cookie. This runs in the browser and decides only which screen to paint —
 * the API re-verifies the JWT on every single request (`verifyRequest` in the
 * Lambda runtime) with the IAM policies in packages/database behind that, so
 * nothing here can widen access. A forged session buys you a rendered shell
 * that 403s on contact.
 *
 * What it buys instead is that the gate resolves from local storage without a
 * network round trip, which is what keeps the redirect ahead of first paint
 * rather than a spinner behind it.
 *
 * The call is still async and still worth awaiting: supabase-js refreshes an
 * expired token here, and that is the moment the session either comes back or
 * is genuinely gone.
 */
export async function currentUser(): Promise<User | null> {
  const {
    data: { session },
  } = await createClient().auth.getSession()

  return session?.user ?? null
}
